import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowLeft, ShoppingBag, Hash, Copy } from 'lucide-react';
import { useCheckoutStore } from "../store/Store";
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd'; 

// --- Sub-Component: CleanAnimatedCheck (Unchanged) ---
const CleanAnimatedCheck = () => {
    return (
        <div className="relative flex items-center justify-center w-16 h-16 mb-4">
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 rounded-full border-2 border-gray-200"
            />
            <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                    delay: 0.3
                }}
                className="relative"
            >
                <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
                    <motion.div
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ delay: 0.6, duration: 0.3, ease: "easeOut" }}
                    >
                        <Check size={24} className="text-white" strokeWidth={3} />
                    </motion.div>
                </div>
                <motion.div
                    initial={{ scale: 0, opacity: 0.4 }}
                    animate={{ scale: 1.5, opacity: 0 }}
                    transition={{
                        duration: 0.6,
                        delay: 0.8,
                        repeat: 1,
                        repeatDelay: 0.5
                    }}
                    className="absolute inset-0 rounded-full border border-black"
                />
            </motion.div>
        </div>
    );
};

// --- Confetti Component (Unchanged) ---
const Confetti = () => {
    const confettiPieces = Array.from({ length: 80 }).map((_, i) => ({
        id: i,
        size: Math.random() * 8 + 4,
        delay: Math.random() * 0.5,
        duration: 3 + Math.random() * 2,
        color: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'][Math.floor(Math.random() * 6)],
        shape: Math.random() > 0.5 ? 'circle' : 'rect',
        x: Math.random() * 100,
        rotation: Math.random() * 720
    }));

    return (
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-[100]">
            {confettiPieces.map((piece) => (
                <motion.div
                    key={piece.id}
                    initial={{ y: -50, x: `${piece.x}vw`, opacity: 0, rotate: 0, scale: 0 }}
                    animate={{
                        y: '110vh',
                        x: `${piece.x + (Math.random() * 40 - 20)}vw`,
                        opacity: [0, 1, 1, 0],
                        rotate: piece.rotation + 1440,
                        scale: [0, 1, 1, 0.5]
                    }}
                    transition={{ duration: piece.duration, delay: piece.delay, ease: "easeOut" }}
                    className="absolute"
                    style={{
                        width: piece.size,
                        height: piece.shape === 'rect' ? piece.size * 0.5 : piece.size,
                        borderRadius: piece.shape === 'circle' ? '50%' : '1px',
                        backgroundColor: piece.color,
                        left: 0,
                    }}
                />
            ))}
        </div>
    );
};

// --- Main CleanOrderConfirm Component ---
const CleanOrderConfirm = () => {
    const [isHovered, setIsHovered] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [showConfetti, setShowConfetti] = useState(true);
    const [isHydrated, setIsHydrated] = useState(false); 

    const { orderId } = useCheckoutStore();
    const navigate = useNavigate();

    // 1. Wait for Zustand to load from sessionStorage
    useEffect(() => {
        const timer = setTimeout(() => setIsHydrated(true), 150);
        return () => clearTimeout(timer);
    }, []);

    // 2. The Bounce Logic: If someone types the URL manually and orderId is missing
    useEffect(() => {
        if (isHydrated && !orderId) {
            if (window.history.length > 1) {
                navigate(-1); 
            } else {
                navigate('/shop', { replace: true });
            }
        }
    }, [isHydrated, orderId, navigate]);

    // 3. Confetti Timer (Existing)
    useEffect(() => {
        const timer = setTimeout(() => setShowConfetti(false), 6000);
        return () => clearTimeout(timer);
    }, []);

    const handleCopyOrderId = async () => {
    if (!orderId) return;
    setIsCopying(true);

    // 1. Try the modern API first
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(orderId);
        } catch (err) {
            console.error("Modern copy failed, trying fallback", err);
            fallbackCopy(orderId);
        }
    } else {
        // 2. Use fallback for non-secure contexts or older browsers
        fallbackCopy(orderId);
    }

    setTimeout(() => setIsCopying(false), 1500);
};

// Fallback function using a hidden textarea
const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    
    // Ensure the textarea is not visible but part of the DOM
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    document.body.appendChild(textArea);
    
    textArea.focus();
    textArea.select();

    try {
        document.execCommand('copy');
    } catch (err) {
        console.error('Fallback copy failed', err);
    }

    document.body.removeChild(textArea);
};

    const handleBackToShop = () => {
        navigate('/shop');
    };

    // While checking hydration, show a clean loader so they don't see an empty "Order ID" card
    if (!isHydrated) {
        return (
            <div className="h-screen w-full flex items-center justify-center">
                <Spin size="large" />
            </div>
        );
    }

    if (!orderId) return null;

    return (
        <div className="h-[calc(100dvh-138px)] sm:h-[calc(100dvh-72px)] w-full flex items-center justify-center p-4 select-none relative overflow-hidden mt-[30px] sm:mt-[0px] ">
            {showConfetti && <Confetti />}

            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="w-full max-w-xs relative z-30"
            >
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="p-5">
                        {/* Header */}
                        <div className="flex flex-col items-center mb-5">
                            <CleanAnimatedCheck />
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="text-center"
                            >
                                <h1 className="text-lg font-bold text-gray-900 mb-1">Order Confirmed</h1>
                                <p className="text-xs text-gray-500">Your order has been placed successfully</p>
                            </motion.div>
                        </div>

                        {/* Order ID Section */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.7 }}
                            className="mb-6"
                        >
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-2 h-2 rounded-full bg-gray-400" />
                                        <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Order ID</span>
                                    </div>
                                </div>

                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.9 }}
                                    className={`flex items-center justify-between rounded-lg p-3 border transition-all duration-200 ${
                                        isCopying ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100'
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors ${
                                            isCopying ? 'bg-green-100' : 'bg-black/5'
                                        }`}>
                                            <Hash size={12} className={isCopying ? 'text-green-600' : 'text-gray-600'} />
                                        </div>
                                        <div className={`font-mono text-sm font-bold tracking-wider transition-colors ${
                                            isCopying ? 'text-green-700' : 'text-gray-900'
                                        }`}>
                                            {orderId}
                                        </div>
                                    </div>

                                    <motion.button
                                        whileTap={{ scale: 0.9 }}
                                        onClick={handleCopyOrderId}
                                        disabled={isCopying}
                                        className={`relative w-7 h-7 rounded-md flex items-center justify-center transition-all duration-200 border ${
                                            isCopying ? 'bg-green-500 border-green-500' : 'bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                                        }`}
                                    >
                                        {isCopying ? (
                                            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                                                <Check size={14} className="text-white" />
                                            </motion.div>
                                        ) : (
                                            <Copy size={14} className="text-gray-700" />
                                        )}
                                    </motion.button>
                                </motion.div>

                                <div className="flex items-center justify-center gap-2 pt-1">
                                    <span className="text-[10px] text-gray-500 font-medium">Order is being processed</span>
                                    <div className="flex gap-1">
                                        {[0, 1, 2].map(i => (
                                            <motion.div
                                                key={i}
                                                animate={{ opacity: [0.2, 1, 0.2] }}
                                                transition={{ duration: 1.2, delay: i * 0.2, repeat: Infinity }}
                                                className="w-1.5 h-1.5 rounded-full bg-gray-400"
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Action button */}
                        <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}>
                            <motion.button
                                onHoverStart={() => setIsHovered(true)}
                                onHoverEnd={() => setIsHovered(false)}
                                onClick={handleBackToShop}
                                whileTap={{ scale: 0.97 }}
                                className="w-full group relative bg-black text-white rounded-lg overflow-hidden"
                            >
                                <div className="h-11 flex items-center justify-center gap-2">
                                    <motion.div animate={{ x: isHovered ? -2 : 0 }} transition={{ type: "spring", stiffness: 500 }}>
                                        <ArrowLeft size={16} />
                                    </motion.div>
                                    <span className="text-sm font-medium">Back to Shopping</span>
                                    <ShoppingBag size={16} />
                                    <motion.div
                                        initial={{ x: '-100%' }}
                                        animate={{ x: isHovered ? '100%' : '-100%' }}
                                        transition={{ duration: 0.5 }}
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                                    />
                                </div>
                            </motion.button>
                        </motion.div>

                        {/* Footer */}
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="mt-5 pt-4 border-t border-gray-100 text-center">
                            <p className="text-[10px] text-gray-400 mb-1">Confirmation email has been sent</p>
                            <span className="text-[10px] text-gray-500">Estimated delivery: 2-4 days</span>
                        </motion.div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default CleanOrderConfirm;